import { T } from "../lib/fc3i18n";
import React, { useState } from "react";
import { motion, MotionConfig } from "framer-motion";
import { ArrowRight, MapPin, Music2, Dumbbell, Flower2, Footprints, Plus, Minus, Timer } from "lucide-react";
import FitnessHub from "../fitness/FitnessHub";
import { WORKOUTS } from "../fitness/data";
const SANS = {
  fontFamily: "var(--fc-sans)",
  fontWeight: 800
};
const EASE = [0.22, 1, 0.36, 1];
const FAQS = [{
  q: "Is FemCare Fitness only for women?",
  a: "Yes. FemCare Fitness is designed for women — workouts, cycle-phase suggestions and safety notes are written with women's bodies and life stages in mind."
}, {
  q: "Is it included in FemCare Premium?",
  a: "Yes. The full Fitness library is included in FemCare Premium at no extra cost. Several workouts and the gym finder are free for everyone."
}, {
  q: "Do I need equipment?",
  a: "Most workouts need nothing but some space and a mat. A few strength workouts use water bottles or light dumbbells."
}, {
  q: "How does the gym finder work?",
  a: "It searches OpenStreetMap for gyms, yoga and dance studios near your location or the area you type. Your location is used only in your browser and is not saved by FemCare."
}, {
  q: "Can I exercise during my period or pregnancy?",
  a: "Many women find gentle movement helpful during their period. During pregnancy or after giving birth, please check with your doctor before starting or changing exercise."
}];
const JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a
    }
  }))
};
const HERO_TILES = [{
  icon: Flower2,
  label: "Yoga",
  bg: "#EFEBFF",
  c: "#5A49E0",
  z: 60,
  x: "4%",
  y: "8%"
}, {
  icon: Music2,
  label: "Dance & Zumba-style",
  bg: "#FFE9EF",
  c: "#D02868",
  z: 120,
  x: "52%",
  y: "2%"
}, {
  icon: Dumbbell,
  label: "Strength",
  bg: "#FFF1DC",
  c: "#B4630E",
  z: 90,
  x: "60%",
  y: "56%"
}, {
  icon: Footprints,
  label: "Walking plans",
  bg: "#EEF8DF",
  c: "#4E7A12",
  z: 140,
  x: "2%",
  y: "80%"
}];
export default function Fitness() {
  const [openFaq, setOpenFaq] = useState(0);
  const free = WORKOUTS.filter(w => w.free).length;
  return <MotionConfig reducedMotion="user">
      <div className="bg-[#FFF8F9] text-[#1E1A2B] overflow-x-clip" data-testid="fitness-page">
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(JSONLD)
      }} />

        {/* HERO */}
        <section className="relative fc-hero-light overflow-hidden">
          <div className="max-w-[1240px] mx-auto px-5 lg:px-8 pt-10 pb-16 lg:pt-14 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <motion.p initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              ease: EASE
            }} className="inline-flex items-center gap-2 rounded-full bg-white border border-[#F3D5DF] px-4 py-2 text-[13px] font-bold text-[#D02868]">{T("New · FemCare Fitness · Women only")}</motion.p>
              <motion.h1 initial={{
              opacity: 0,
              y: 24
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.9,
              ease: EASE,
              delay: 0.08
            }} className="mt-6 text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.04] tracking-[-0.035em]" style={SANS}>{T("Move in a way that")} <span className="fc-text-coral">{T("fits your cycle")}</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.9,
              ease: EASE,
              delay: 0.2
            }} className="mt-6 text-[18px] lg:text-[20px] leading-relaxed text-[#5F5A70] max-w-xl">{T("Yoga, dance cardio, strength, Pilates and walking plans with a built-in timer and voice guidance — plus gyms and studios near you. Included in FemCare Premium.")}</motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.9,
              ease: EASE,
              delay: 0.32
            }} className="mt-9 flex flex-wrap gap-3">
                <a href="#library" className="fc-btn-coral inline-flex items-center gap-2 min-h-[56px] rounded-full px-8 text-[17px] font-extrabold">{T("Try {{n}} free workouts", { n: free })} <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#gyms" className="inline-flex items-center gap-2 min-h-[56px] rounded-full px-7 text-[16px] font-bold text-[#2B2140] bg-white border border-[#E9DCE3] hover:border-[#2B2140] transition-colors">
                  <MapPin className="w-5 h-5 text-[#6B5BEF]" /> {T("Find gyms near me")}</a>
              </motion.div>
            </div>
            <div className="lg:col-span-6 relative h-[380px] sm:h-[440px] lg:h-[520px] fc-hstage" aria-hidden="true">
              <div className="fc-hblob fc-hblob--a" /><div className="fc-hblob fc-hblob--b" /><div className="fc-hblob fc-hblob--c" />
              <div className="absolute inset-0 fc-hrig" style={{
              "--rx": "8deg",
              "--ry": "-14deg"
            }}>
                <div className="fc-hring"><div className="fc-hring-band" /><span className="fc-hring-today" /></div>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[230px] sm:w-[260px] rounded-[34px] bg-white p-6 shadow-[0_50px_90px_-40px_rgba(43,33,64,0.55)]" style={{
                transform: "translate(-50%,-50%) translateZ(70px)"
              }}>
                  <p className="text-[12px] font-extrabold uppercase tracking-wider text-[#E0306F]">{T("Now playing")}</p>
                  <p className="mt-1 text-[20px] text-[#1E1A2B] leading-tight" style={SANS}>{T("Dance Cardio for Beginners")}</p>
                  <div className="relative mx-auto mt-4 w-[130px] h-[130px]">
                    <svg viewBox="0 0 130 130" className="w-full h-full -rotate-90"><circle cx="65" cy="65" r="54" fill="none" stroke="#F3EEF6" strokeWidth="10" /><circle cx="65" cy="65" r="54" fill="none" stroke="#E0306F" strokeWidth="10" strokeLinecap="round" strokeDasharray="339" strokeDashoffset="110" /></svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[30px] font-extrabold tabular-nums">0:42</span>
                  </div>
                  <p className="mt-3 text-center text-[15px] font-extrabold">{T("Salsa basic")}</p>
                  <p className="mt-1 flex items-center justify-center gap-1 text-[12px] font-bold text-[#7A7488]"><Timer className="w-3.5 h-3.5" /> {T("Next: Cha-cha side steps")}</p>
                </div>
                {HERO_TILES.map(({
                icon: I,
                label,
                bg,
                c,
                z,
                x,
                y
              }, i) => <div key={label} className="fc-hcard" style={{
                left: x,
                top: y,
                "--z": `${z}px`,
                animationDelay: `${0.4 + i * 0.12}s`
              }}>
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                  background: bg,
                  color: c
                }}><I className="w-5 h-5" /></span>
                    <span className="text-[15px] font-extrabold text-[#1E1A2B]">{label}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        <div id="library" className="max-w-[1240px] mx-auto px-5 lg:px-8 py-16 lg:py-24 scroll-mt-24">
          <FitnessHub unlocked={false} lockedHref="/app/fitness" />
        </div>

        {/* FAQ */}
        <section className="max-w-[920px] mx-auto px-5 lg:px-8 pb-20 lg:pb-28" data-testid="fitness-faq">
          <h2 className="text-center text-[32px] sm:text-[40px] tracking-[-0.025em]" style={SANS}>{T("Fitness FAQ")}</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map((f, i) => {
            const o = openFaq === i;
            return <div key={f.q} className={`rounded-[22px] border ${o ? "bg-white border-[#EADFF7]" : "bg-white/60 border-[#F1E6EA]"}`}>
                  <h3>
                    <button type="button" onClick={() => setOpenFaq(o ? -1 : i)} aria-expanded={o} className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left text-[17px] font-bold">
                      {T(f.q)}
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${o ? "bg-[#6B5BEF] text-white" : "bg-[#F6F3FF] text-[#6B5BEF]"}`}>{o ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</span>
                    </button>
                  </h3>
                  {o && <p className="px-6 pb-6 text-[16px] leading-relaxed text-[#5F5A70]">{T(f.a)}</p>}
                </div>;
          })}
          </div>
        </section>
      </div>
    </MotionConfig>;
}